import React, { useState } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { AuthService } from "../login/service";
import { enqueueSnackbar } from "notistack";
import { handleError } from "../../shared/functions/handleError";

interface IResetPasswordForm {
    password: string;
    confirmPassword: string;
}

const ResetPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validationSchema = yup.object().shape({
        password: yup
            .string()
            .required("validation.required")
            .min(8, "validation.passwordMin"),
        confirmPassword: yup
            .string()
            .required("validation.required")
            .oneOf([yup.ref("password")], "validation.passNotMatch"),
    });

    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IResetPasswordForm>({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            password: "",
            confirmPassword: "",
        },
    });

    const onSubmit = async (data: IResetPasswordForm) => {
        if (!token) {
            enqueueSnackbar(t("auth.invalidResetToken"), { variant: "error" });
            return;
        }

        try {
            await AuthService.resetPassword(token, data.password, data.confirmPassword);
            setIsSubmitted(true);
            enqueueSnackbar(t("auth.passwordResetSuccess"), { variant: "success" });
        } catch (error: any) {
            handleError(error);
        }
    };

    if (!token) {
        return (
            <React.Fragment>
                <div className="auth-main v1">
                    <div className="auth-wrapper">
                        <div className="auth-form">
                            <Card className="my-5">
                                <Card.Body>
                                    <div className="text-center">
                                        <div className="mb-4">
                                            <i className="ph-duotone ph-warning text-danger" style={{ fontSize: '4rem' }}></i>
                                        </div>
                                        <h5 className="mb-3">{t("auth.invalidResetToken")}</h5>
                                        <p className="text-muted mb-4">
                                            {t("auth.invalidResetTokenDescription")}
                                        </p>
                                        <Link to="/forgot-password" className="btn btn-primary">
                                            {t("auth.requestNewLink")}
                                        </Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <div className="auth-main v1">
                <div className="auth-wrapper">
                    <div className="auth-form">
                        <Card className="my-5">
                            <Card.Body>
                                {isSubmitted ? (
                                    <div className="text-center">
                                        <div className="mb-4">
                                            <i className="ph-duotone ph-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                                        </div>
                                        <h5 className="mb-3">{t("auth.passwordResetSuccess")}</h5>
                                        <p className="text-muted mb-4">
                                            {t("auth.passwordResetSuccessDescription")}
                                        </p>
                                        <Link to="/" className="btn btn-primary">
                                            {t("auth.backToLogin")}
                                        </Link>
                                    </div>
                                ) : (
                                    <>
                                        <div className="text-center">
                                            <img
                                                src={"/assets/images/authentication/img-auth-login.png"}
                                                alt="images"
                                                className="img-fluid mb-3"
                                            />
                                            <h4 className="f-w-500 mb-1">{t("auth.resetPasswordTitle")}</h4>
                                            <p className="mb-3 text-muted">
                                                {t("auth.resetPasswordDescription")}
                                            </p>
                                        </div>
                                        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
                                            <Form.Group className="mb-3" controlId="password">
                                                <Form.Label>{t("general.password")}</Form.Label>
                                                <Controller
                                                    name="password"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="password"
                                                            placeholder={t("general.password")}
                                                            isInvalid={!!errors.password?.message}
                                                        />
                                                    )}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.password?.message &&
                                                        t(errors.password.message as string)}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <Form.Group className="mb-3" controlId="confirmPassword">
                                                <Form.Label>{t("general.confirmPassword")}</Form.Label>
                                                <Controller
                                                    name="confirmPassword"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Form.Control
                                                            {...field}
                                                            type="password"
                                                            placeholder={t("general.confirmPassword")}
                                                            isInvalid={!!errors.confirmPassword?.message}
                                                        />
                                                    )}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {errors.confirmPassword?.message &&
                                                        t(errors.confirmPassword.message as string)}
                                                </Form.Control.Feedback>
                                            </Form.Group>

                                            <div className="d-grid mt-4">
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={isSubmitting}
                                                >
                                                    {isSubmitting ? t("auth.resetting") : t("auth.resetPassword")}
                                                </button>
                                            </div>

                                            <div className="text-center mt-3">
                                                <Link to="/">
                                                    <h6 className="f-w-400 mb-0">
                                                        {t("auth.backToLogin")}
                                                    </h6>
                                                </Link>
                                            </div>
                                        </Form>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </div>

                    <div className="auth-sidefooter">
                        <Row>
                            <Col md={2}>
                                <img
                                    src={"/assets/images/logo-horizontal.png"}
                                    className="img-brand img-fluid w-100"
                                    alt="images"
                                />
                            </Col>
                            <Col md={10} className="my-auto">
                                <p className="m-0">{t("general.developedBy")}</p>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ResetPassword;
